/**
 * Compact strip showing the most-recent public grow logs for a strain or
 * breeder. Embedded on:
 *   • LineageDetail (filters by `strain_id_or_slug`)
 *   • BreederStorefront (filters by `vendor_id_or_slug`)
 *
 * Hides itself when there are no public logs so unfeatured pages stay clean.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sprout, ArrowRight } from "lucide-react";
import { api } from "../lib/apiClient";
import { GrowLogCard } from "../pages/GrowLogs";

export default function GrowLogStrip({ strainIdOrSlug, vendorIdOrSlug, title = "Grow logs", limit = 6 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = { limit };
    if (strainIdOrSlug) params.strain_id = strainIdOrSlug;
    if (vendorIdOrSlug) params.vendor_id = vendorIdOrSlug;
    if (!strainIdOrSlug && !vendorIdOrSlug) { setLoading(false); return; }
    setLoading(true);
    api.get("/grow-logs", { params })
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [strainIdOrSlug, vendorIdOrSlug, limit]);

  if (loading) return null;
  if (!items.length) return null;

  const allHref = strainIdOrSlug
    ? `/grow-logs?strain_id=${encodeURIComponent(strainIdOrSlug)}`
    : `/grow-logs?vendor_id=${encodeURIComponent(vendorIdOrSlug)}`;

  return (
    <section className="mt-10" data-testid="grow-log-strip">
      <div className="flex items-end justify-between gap-3 mb-4">
        <div>
          <div className="label-eyebrow mb-1" style={{ color: "var(--gold)" }}>
            <Sprout className="w-3 h-3 inline -mt-0.5 mr-1" /> Proof layer
          </div>
          <h3 className="font-serif text-2xl tracking-tighter" style={{ color: "var(--text)" }}>
            {title}
          </h3>
        </div>
        <Link to={allHref}
              className="text-[11px] font-mono uppercase tracking-[0.18em] inline-flex items-center gap-1"
              style={{ color: "var(--text-muted)" }}
              data-testid="grow-log-strip-all-link">
          All grows <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((l) => <GrowLogCard key={l.id} log={l} />)}
      </div>
    </section>
  );
}
